import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';
import { tc } from '../../../lib/trackingContext';
import { getUserFromCookies } from '../../useUser';
import { AccountDesignerPreferencesVertical } from '../../useDesigners';
import { useAuthModal } from '../../useAuthModal';
import { GET_DESIGNER_PREFERENCES_QUERY } from '../useDesignerPreferences.rest';
import { FOLLOW_DESIGNER_MUTATION } from './useFollowDesigner.rest';

export const useFollowDesigner = (vertical: AccountDesignerPreferencesVertical) => {
  const { openAuthModal } = useAuthModal();
  const [mutate, { loading, error }] = useMutation(FOLLOW_DESIGNER_MUTATION);

  const followDesignerWhenLoggedIn = useCallback(
    async (designerId: string, designerName: string | null) => {
      const user = getUserFromCookies();
      await mutate({
        variables: {
          input: {
            designer_id: Number(designerId),
            user_id: Number(user.id),
            inference_type: 'explicit',
            action: 'follow',
            vertical
          }
        },
        refetchQueries: [
          {
            query: GET_DESIGNER_PREFERENCES_QUERY,
            variables: {
              userId: user.id,
              vertical
            }
          }
        ]
      }).then(() => {
        tc.track('Designer Followed', {
          addToScopeOnce: {
            designerName,
            designerId,
            vertical
          },
          addToTrackingEvent: { label: designerName }
        });
      });
    },
    [mutate, vertical]
  );

  const followDesigner = useCallback(
    async (designerId: string, designerName: string | null) => {
      const user = getUserFromCookies();
      return user.isLoggedIn
        ? await followDesignerWhenLoggedIn(designerId, designerName)
        : openAuthModal({ onSuccess: () => followDesignerWhenLoggedIn(designerId, designerName) });
    },
    [followDesignerWhenLoggedIn, openAuthModal]
  );

  return {
    followDesigner,
    loading,
    error
  };
};
